import type {
  NotificationCategory,
  NotificationPreference,
  NotificationPreferenceGroup,
} from '@seek/ca-graphql-schema/types';
import type { WithTypeName } from 'src/types/global';

export const categoryUpdates: WithTypeName<NotificationCategory> = {
  code: 'updates',
  description: 'Job application updates',
  __typename: 'NotificationCategory',
};

export const categoryOpportunities: WithTypeName<NotificationCategory> = {
  code: 'opportunities',
  description: 'New opportunities for you',
  __typename: 'NotificationCategory',
};

export const AppViewedSubscription: WithTypeName<NotificationPreference> = {
  product: {
    code: 'AppViewed',
    label: 'Application viewed',
    description:
      'Find out when your job application has been viewed by the employer.',
    __typename: 'NotificationProduct',
  },
  // @ts-ignore
  subscriptions: [
    {
      id: 'AppViewed:email',
      channel: {
        __typename: 'NotificationChannel',
        code: 'email',
        label: 'Email',
      },
      subscribed: true,
      frequency: null,
      __typename: 'NotificationSubscription',
    },
    {
      id: 'AppViewed:push',
      channel: {
        __typename: 'NotificationChannel',
        code: 'push',
        label: 'Push',
      },
      subscribed: false,
      frequency: null,
      __typename: 'NotificationSubscription',
    },
  ],
  __typename: 'NotificationPreference',
};

export const AppConfirmedSubscription: WithTypeName<NotificationPreference> = {
  product: {
    code: 'AppConfirmed',
    label: 'Application confirmation',
    description:
      'Confirmation that your job application has been sent to the employer.',
    __typename: 'NotificationProduct',
  },
  // @ts-ignore
  subscriptions: [
    {
      id: 'AppConfirmed:email',
      channel: {
        code: 'email',
        label: 'Email',
        __typename: 'NotificationChannel',
      },
      subscribed: true,
      frequency: null,
      __typename: 'NotificationSubscription',
    },
  ],
  __typename: 'NotificationPreference',
};

export const notificationSubscriptionFrequencyOptions: Array<{
  __typename?: 'NotificationFrequencyOptions' | undefined;
  code: string;
  label: string;
  description: string | null;
}> = [
  {
    code: 'dynamic',
    label: 'Dynamic',
    description:
      'More frequent emails when you’re actively looking and less when you’re not.',
    __typename: 'NotificationFrequencyOptions',
  },
  {
    code: 'daily',
    label: 'Daily',
    description: null,
    __typename: 'NotificationFrequencyOptions',
  },
  {
    code: 'weekly',
    label: 'Weekly',
    description: null,
    __typename: 'NotificationFrequencyOptions',
  },
];

export const notificationPreferenceGroups: WithTypeName<
  NotificationPreferenceGroup[]
> = [
  {
    __typename: 'NotificationPreferenceGroup',
    category: categoryUpdates,
    notificationPreferences: [
      AppConfirmedSubscription,
      AppViewedSubscription,
      {
        product: {
          code: 'AppNotSuitable',
          label: 'Application unlikely to progress',
          description:
            'Find out if the employer has indicated that your application is unlikely to progress.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'AppNotSuitable:email',
            channel: {
              code: 'email',
              label: 'Email',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
          {
            id: 'AppNotSuitable:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: false,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'AppAdExpired',
          label: 'Job ad expired',
          description:
            'Find out when the job you have applied for is no longer being advertised.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'AppAdExpired:email',
            channel: {
              code: 'email',
              label: 'Email',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
    ],
  },
  {
    __typename: 'NotificationPreferenceGroup',
    category: categoryOpportunities,
    notificationPreferences: [
      {
        product: {
          code: 'WRU',
          label: 'Weekly job recommendations',
          description:
            'SEEK Weekly Round Up features job recommendations and industry insights based on your recent activity and SEEK Profile.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'WRU:email',
            channel: {
              code: 'email',
              label: 'Email',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: {
              value: 'dynamic',
              // @ts-ignore
              options: notificationSubscriptionFrequencyOptions,
              __typename: 'NotificationFrequency',
            },
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'NewJob',
          label: 'Jobs just for you',
          description:
            'Get the latest jobs recommended for you, sent straight to your mobile.',
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'NewJob:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: false,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
      {
        product: {
          code: 'ReminderToApply',
          label: 'Reminder to apply',
          description:
            "If you save a job but don't apply for it within the week, we'll send a reminder via push notification.",
          __typename: 'NotificationProduct',
        },
        // @ts-ignore
        subscriptions: [
          {
            id: 'ReminderToApply:push',
            channel: {
              code: 'push',
              label: 'Push',
              __typename: 'NotificationChannel',
            },
            subscribed: true,
            frequency: null,
            __typename: 'NotificationSubscription',
          },
        ],
        __typename: 'NotificationPreference',
      },
    ],
  },
];
